import React, { useState, useEffect } from 'react';
import {
  ComputerDesktopIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  BoltIcon,
  CursorArrowRaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import StatCard from '../components/Dashboard/StatCard';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const GERMAN_TEXT_POOL = [
  "Deutsch zu lernen macht unglaublich viel Spaß und eröffnet viele Möglichkeiten im Berufsleben.",
  "Die Grammatik mag am Anfang kompliziert erscheinen, aber sie folgt sehr klaren Regeln.",
  "Können Sie mir bitte den Weg zum nächsten Bahnhof zeigen? Ich muss den Zug nach Berlin nehmen.",
  "In Deutschland gibt es viele verschiedene Dialekte, aber Hochdeutsch wird überall verstanden.",
  "Das Wetter heute ist wunderschön, die Sonne scheint und es gibt kaum Wolken am blauen Himmel.",
  "Viele Studenten aus der ganzen Welt kommen nach Berlin, um an den exzellenten Universitäten zu studieren.",
  "Gestern Abend habe ich mit meinen Freunden in einem gemütlichen Restaurant in der Innenstadt gegessen.",
  "Man sagt, dass Übung den Meister macht, und das gilt besonders für das Erlernen einer neuen Sprache.",
  "Die Alpen im Süden Deutschlands bieten atemberaubende Landschaften zum Wandern und Skifahren an.",
  "Ich freue mich darauf, im nächsten Sommer eine lange Reise durch ganz Europa zu machen.",
  "Es ist wichtig, jeden Tag ein wenig zu üben, um flüssiger sprechen und schreiben zu können.",
  "Technik und Innovation sind zwei Bereiche, in denen Deutschland weltweit eine führende Rolle spielt.",
  "Haben Sie schon einmal eine echte deutsche Currywurst probiert? Sie ist sehr typisch für die Hauptstadt.",
  "Die Komplexität der deutschen Sprache erfordert viel Geduld, aber die Mühe lohnt sich auf jeden Fall."
];

const TypingPracticePage = () => {
  const [targetText, setTargetText] = useState(GERMAN_TEXT_POOL.join(' '));
  const [userInput, setUserInput] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [stats, setStats] = useState({ wpm: 0, accuracy: 100, errors: 0 });

  const resetPractice = () => {
    setUserInput('');
    setIsStarted(false);
    setStartTime(null);
    setIsCompleted(false);
    setStats({ wpm: 0, accuracy: 100, errors: 0 });
    setTargetText(GERMAN_TEXT_POOL.sort(() => Math.random() - 0.5).join(' '));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isCompleted) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (!isStarted && e.key.length === 1) {
        setIsStarted(true);
        setStartTime(Date.now());
      }

      if (e.key === 'Backspace') {
        setUserInput(prev => prev.slice(0, -1));
        return;
      }

      if (e.key.length !== 1) return;

      setUserInput(prev => {
        if (prev.length >= targetText.length) return prev;
        const next = prev + e.key;
        if (next === targetText) setIsCompleted(true);
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStarted, isCompleted, targetText]);

  useEffect(() => {
    if (userInput.length === 0) return;

    let errorCount = 0;
    for (let i = 0; i < userInput.length; i++) {
      if (userInput[i] !== targetText[i]) errorCount++;
    }

    const accuracy = Math.max(0, Math.round(((userInput.length - errorCount) / userInput.length) * 100));

    let wpm = 0;
    if (startTime) {
      const timeElapsedMinutes = (Date.now() - startTime) / 60000;
      const wordsTyped = userInput.length / 5;
      wpm = Math.round(wordsTyped / (timeElapsedMinutes || 1));
    }

    setStats({ wpm, accuracy, errors: errorCount });
  }, [userInput, startTime, targetText]);

  const renderText = () => {
    return targetText.split('').map((char, index) => {
      let color = 'text-gray-300';
      if (index < userInput.length) {
        color = userInput[index] === char ? 'text-slate-900 border-b-2 border-green-500' : 'text-white bg-red-500 rounded';
      } else if (index === userInput.length) {
        color = 'text-slate-900 border-b-2 border-brand-primary/40 bg-brand-primary/10 animate-pulse';
      }

      return (
        <span key={index} className={`font-mono text-2xl transition-all duration-75 ${color}`}>
          {char}
        </span>
      );
    });
  };

  return (
    <DashboardLayout>
      <div className="page-shell-wide font-body min-h-screen">
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-2xl font-black text-brand-text flex items-center gap-3">
              <ComputerDesktopIcon className="w-8 h-8 text-brand-primary" />
              Shtypi Profesional
            </h1>
            <p className="text-xs text-brand-text/50 font-bold uppercase tracking-widest mt-1">Gjermanisht • Master Mode</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={resetPractice}
            className="text-xs font-bold"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" /> Rifillo Sfidën
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StatCard
            label="Shpejtësia (WPM)"
            value={stats.wpm}
            icon={BoltIcon}
            change="+5%"
            isPositive={true}
          />
          <StatCard
            label="Saktësia"
            value={`${stats.accuracy}%`}
            icon={CursorArrowRaysIcon}
            change={stats.accuracy > 95 ? '+0.2%' : '-1.5%'}
            isPositive={stats.accuracy > 95}
          />
          <StatCard
            label="Koha në Lëvizje"
            value={startTime ? `${Math.round((Date.now() - startTime) / 1000)}s` : '0s'}
            icon={ClockIcon}
          />
        </div>

        <Card className="p-10 md:p-16 rounded-[3rem] relative overflow-hidden group">
          {!isStarted && !isCompleted && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[2px] z-10">
              <Button className="text-xs font-black uppercase tracking-widest animate-bounce">
                Filloni të shkruani...
              </Button>
            </div>
          )}

          <div className="relative z-0 leading-[1.8] tracking-tight selection:bg-brand-primary/20">
            {renderText()}
          </div>

          {isCompleted && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center z-20 animate-in fade-in zoom-in duration-500">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 border border-green-100">
                <CheckCircleIcon className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-black text-brand-text mb-2">Sfidë e Përfunduar!</h2>
              <p className="text-brand-text/60 font-medium mb-8 max-w-md text-center">
                Keni shkruar me sukses të gjithë tekstin me një saktësi prej {stats.accuracy}%.
              </p>
              <Button
                onClick={resetPractice}
                className="px-10 py-4 font-black text-sm hover:scale-105 transition-all shadow-xl shadow-black/10"
              >
                Provo Tekst Tjetër
              </Button>
            </div>
          )}
        </Card>

        <div className="mt-12 flex justify-center items-center gap-6 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
           <div className="h-1 w-12 bg-black rounded-full" />
           <div className="h-1 w-12 bg-red-600 rounded-full" />
           <div className="h-1 w-12 bg-brand-primary rounded-full" />
           <span className="text-[10px] font-black text-brand-text/50 uppercase tracking-widest">Master German Typing</span>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TypingPracticePage;
